import { Clock3, Ellipsis, Layers } from 'lucide-react';
import { Fragment, memo } from 'react';

import {
    type CalendarChipBucket,
    formatShiftRecurrenceLabel,
    getCalendarShiftSelectionEligibility,
    getShiftChipCaregiverLabel,
    getShiftChipOutreachCard,
    getShiftGroupSize,
    getShiftPopoverAriaProps,
    resolveShiftSurfaceInteraction,
    type ShiftChipOutreachCard,
    type ShiftChipOutreachRail,
    type ShiftChipOutreachStat,
    type ShiftOutreachStatus,
} from '@apps/web/components/shifts_calendar/calendar_model';
import { getShiftChipOutcomeLabel } from '@apps/web/components/shifts_calendar/shift_card_model';
import { ShiftNoteTooltip } from '@apps/web/components/shifts_calendar/shift_note_tooltip';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatCompactTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type Shift } from '@libraries/typescript/api_client';

export const ShiftChip = memo(function ShiftChip({
    shift,
    timezone,
    navigationRowKey,
    navigationDayKey,
    outreachStatus,
    popoverShiftId,
    isRovingTarget,
    labelsClient,
    isSelectable,
    isSelected,
    isExcluded,
    isAtSelectionCap,
    maximumSelectedShifts,
    onToggleShift,
    onSelectShift,
}: {
    readonly shift: Shift;
    readonly timezone: string;
    readonly navigationRowKey: string;
    readonly navigationDayKey: string;
    readonly outreachStatus?: ShiftOutreachStatus;
    readonly popoverShiftId: string | null;
    readonly isRovingTarget: boolean;
    readonly labelsClient: boolean;
    readonly isSelectable: boolean;
    readonly isSelected: boolean;
    readonly isExcluded: boolean;
    readonly isAtSelectionCap: boolean;
    readonly maximumSelectedShifts: number;
    readonly onToggleShift?: (shift: Shift) => void;
    readonly onSelectShift?: (
        shift: Shift,
        trigger: HTMLButtonElement,
        rowKey: string,
    ) => void;
}) {
    const { displayLastFirst, formatName } = useFormatPersonName();
    const isPopoverOpen = popoverShiftId === shift.id;
    const now = new Date();
    const { bucket, palette, isPastOpen, showsCheckbox } =
        resolveShiftSurfaceInteraction({
            shift,
            outreachStatus,
            now,
            isSelectable,
        });
    const timeRange = formatCompactTimeRange(shift, timezone);
    const dateLabel = formatShiftDateLabel(shift, timezone);
    const caregiverLabel = getShiftChipCaregiverLabel(
        shift,
        bucket,
        displayLastFirst,
    );
    const chipLabel = labelsClient
        ? formatName(shift.patient_name)
        : caregiverLabel;
    const outcomeLabel = getShiftChipOutcomeLabel(shift, outreachStatus);
    const recurrenceLabel = formatShiftRecurrenceLabel(shift);
    const groupSize = getShiftGroupSize(outreachStatus);
    const outreachCard =
        bucket === 'outreaching' && outreachStatus?.activeFillOutreach
            ? getShiftChipOutreachCard({
                  outreach: outreachStatus.activeFillOutreach,
                  counts: outreachStatus.activeFillResponseCounts,
                  shift,
                  timezone,
                  now: new Date(),
              })
            : null;
    // Bold anywhere on the card always means "a person needs to act on
    // this"; everything settled or still being worked stays quieter.
    const needsAction = isPastOpen
        ? false
        : outreachCard
          ? outreachCard.variant === 'needs_you'
          : NEEDS_ACTION_BUCKETS.has(bucket);
    const qualifiedOutcomeLabel = outcomeLabel
        ? `Prior outreach: ${outcomeLabel}`
        : null;
    const ariaLabel = formatShiftAriaLabel(
        shift,
        dateLabel,
        timeRange,
        caregiverLabel,
        outreachCard
            ? `${palette.label}, ${outreachCard.label}`
            : (qualifiedOutcomeLabel ?? palette.label),
        recurrenceLabel,
        groupSize,
    );

    return (
        <div className="group/shift-chip relative isolate">
            <ShiftNoteTooltip
                shift={shift}
                summary={
                    <Fragment>
                        {outreachCard
                            ? outreachCard.label
                            : (qualifiedOutcomeLabel ?? palette.label)}{' '}
                        · {formatName(shift.patient_name)}
                        {recurrenceLabel ? ` · ${recurrenceLabel}` : ''}
                        {groupSize !== null
                            ? ' · Part of a multi-shift outreach'
                            : ''}
                        {isExcluded
                            ? ` · Not added — ${maximumSelectedShifts} shift limit`
                            : ''}
                    </Fragment>
                }
            >
                <button
                    type="button"
                    data-calendar-shift-chip={true}
                    data-calendar-row-key={navigationRowKey}
                    data-calendar-day-key={navigationDayKey}
                    data-testid="shifts-calendar-chip"
                    data-shift-id={shift.id}
                    data-client-id={shift.client_id ?? undefined}
                    data-client-name={shift.patient_name}
                    data-status-bucket={bucket}
                    data-outreach-stage={outreachCard?.stage}
                    data-past-open={isPastOpen ? true : undefined}
                    data-selected={isSelected ? true : undefined}
                    data-selection-excluded={isExcluded ? true : undefined}
                    tabIndex={isRovingTarget ? 0 : -1}
                    className={cn(
                        'focus-visible:ring-ring flex w-full min-w-0 flex-col gap-0.5 rounded-md border px-2 py-1.5 text-left text-xs shadow-xs transition-shadow focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                        showsCheckbox && 'pr-8',
                        outreachCard
                            ? cn('relative pb-3.5', OUTREACH_CARD_CLASS)
                            : cn(
                                  palette.chipClassName,
                                  palette.outlineClassName,
                              ),
                        bucket === 'cancelled' && 'line-through',
                        isPastOpen && 'cursor-default',
                        isSelected && 'ring-primary/70 ring-2',
                        isExcluded &&
                            'ring-2 ring-amber-500 dark:ring-amber-400',
                    )}
                    aria-label={ariaLabel}
                    {...getShiftPopoverAriaProps({
                        shiftId: shift.id,
                        isPastOpen,
                        isPopoverOpen,
                    })}
                    onClick={(event) => {
                        if (isPastOpen) {
                            return;
                        }
                        // Cmd/ctrl-click toggles selection directly (like
                        // file explorers) instead of opening the popover.
                        if (showsCheckbox && (event.metaKey || event.ctrlKey)) {
                            onToggleShift?.(shift);
                            return;
                        }
                        onSelectShift?.(
                            shift,
                            event.currentTarget,
                            navigationRowKey,
                        );
                    }}
                >
                    {outreachCard ? (
                        <ShiftChipOutreachContent
                            card={outreachCard}
                            timeRange={timeRange}
                            recurring={recurrenceLabel !== null}
                            grouped={groupSize !== null}
                        />
                    ) : (
                        <Fragment>
                            <span className="flex min-w-0 items-center gap-1.5">
                                <span
                                    className={cn(
                                        'shrink-0 tabular-nums',
                                        needsAction && 'font-semibold',
                                    )}
                                >
                                    {timeRange}
                                </span>
                                {bucket === 'clock_issue' ? (
                                    <Clock3
                                        className="size-3 shrink-0"
                                        aria-hidden={true}
                                    />
                                ) : null}
                                {recurrenceLabel !== null ||
                                groupSize !== null ||
                                bucket === 'in_progress' ? (
                                    <span className="ml-auto flex shrink-0 items-center gap-1">
                                        {recurrenceLabel !== null ? (
                                            <span aria-hidden={true}>↻</span>
                                        ) : null}
                                        {groupSize !== null ? (
                                            <Layers
                                                className="size-3 shrink-0 opacity-70"
                                                aria-hidden={true}
                                            />
                                        ) : null}
                                        {bucket === 'in_progress' ? (
                                            <Ellipsis
                                                className="size-3 shrink-0 text-emerald-600 motion-safe:animate-pulse dark:text-emerald-400"
                                                aria-hidden={true}
                                            />
                                        ) : null}
                                    </span>
                                ) : null}
                            </span>
                            {chipLabel ? (
                                <span className="min-w-0 truncate font-medium">
                                    {chipLabel}
                                </span>
                            ) : null}
                        </Fragment>
                    )}
                </button>
            </ShiftNoteTooltip>
            {showsCheckbox ? (
                <ShiftSelectionCheckbox
                    shift={shift}
                    outreachStatus={outreachStatus}
                    dateLabel={dateLabel}
                    timeRange={timeRange}
                    isSelected={isSelected}
                    isAtSelectionCap={isAtSelectionCap}
                    maximumSelectedShifts={maximumSelectedShifts}
                    now={now}
                    onToggle={onToggleShift}
                />
            ) : null}
        </div>
    );
});

const NEEDS_ACTION_BUCKETS: ReadonlySet<CalendarChipBucket> = new Set([
    'open',
    'clock_issue',
    'attention',
]);

// One lavender material for every outreach card; the rail tone and the
// action row carry the difference between Phoebe-working and needs-you.
const OUTREACH_CARD_CLASS =
    'border-[#5533EE]/30 bg-gradient-to-b from-[#D6D3FD] to-[#C9C6FB] text-[#191046] dark:border-[#8C82FF]/50 dark:from-[#241668] dark:to-[#1B0F55] dark:text-[#F5F2FF]';

// Stat tone colors for the top-right trigger.
const OUTREACH_STAT_TONE_CLASSES: Record<
    ShiftChipOutreachStat['tone'],
    string
> = {
    progress: 'text-[#2E2570] dark:text-[#C9C2F2]',
    muted: 'text-[#6C63B8] dark:text-[#A79FE0]',
    good: 'text-[#0B7A44] dark:text-[#34D399]',
    risk: 'text-[#B0450F] dark:text-[#FF9E7A]',
};

// The action verb stays in the card ink (a full tone-colored word clashes on
// the lavender material); the chevron takes the rail tone as a small accent.
const OUTREACH_CHEVRON_TONE_CLASSES: Record<'good' | 'risk', string> = {
    good: 'text-[#0B7A44] dark:text-[#34D399]',
    risk: 'text-[#B0450F] dark:text-[#FF9E7A]',
};

// Ledger-row chip body: line one is facts (time flush left, trigger stat
// flush right, both tabular); line two is meaning — a quiet state word on
// calm cards, a tone-inked action list-row (verb + chevron) on needs_you
// cards. The chevron is the affordance for the popover's primary action, not
// a nested button.
function ShiftChipOutreachContent({
    card,
    timeRange,
    recurring,
    grouped,
}: {
    readonly card: ShiftChipOutreachCard;
    readonly timeRange: string;
    readonly recurring: boolean;
    readonly grouped: boolean;
}) {
    return (
        <Fragment>
            <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="shrink-0 font-semibold tabular-nums">
                    {timeRange}
                </span>
                {recurring ? (
                    <span className="shrink-0" aria-hidden={true}>
                        ↻
                    </span>
                ) : null}
                {grouped ? (
                    <Layers
                        className="size-3 shrink-0 self-center opacity-70"
                        aria-hidden={true}
                    />
                ) : null}
                {card.stat ? (
                    <span
                        className={cn(
                            'ml-auto shrink-0 text-[11px] leading-4 font-bold whitespace-nowrap tabular-nums',
                            OUTREACH_STAT_TONE_CLASSES[card.stat.tone],
                        )}
                    >
                        {card.stat.text}
                        {card.stat.mutedText ? (
                            <span className="font-semibold text-[#6C63B8] dark:text-[#A79FE0]">
                                {card.stat.mutedText}
                            </span>
                        ) : null}
                        {card.stat.suffixText
                            ? ` ${card.stat.suffixText}`
                            : null}
                    </span>
                ) : null}
            </span>
            {card.actionVerb ? (
                <span
                    data-testid="shift-chip-status-line"
                    className="flex min-w-0 items-center justify-between gap-1 text-[11px] leading-4 font-bold"
                >
                    <span className="min-w-0 truncate">{card.actionVerb}</span>
                    <span
                        className={cn(
                            'shrink-0 motion-safe:transition-transform motion-safe:group-hover/shift-chip:translate-x-0.5',
                            OUTREACH_CHEVRON_TONE_CLASSES[
                                card.rail.kind === 'tone'
                                    ? card.rail.tone
                                    : 'risk'
                            ],
                        )}
                        aria-hidden={true}
                    >
                        ›
                    </span>
                </span>
            ) : (
                <span
                    data-testid="shift-chip-status-line"
                    className="min-w-0 truncate text-[11px] leading-4 font-medium text-[#4A4680] dark:text-[#C9C2F2]"
                >
                    {card.factLine}
                </span>
            )}
            <ShiftChipOutreachRailBar rail={card.rail} />
        </Fragment>
    );
}

// Inset, fully-rounded rail along the chip's bottom edge. Live and tone
// rails sweep the sheen (off under reduced motion); tone rails color the
// full track (green = good news waiting, coral = fill at risk). Nub rails
// stay static: a 14px head marks a draft where nothing is moving yet; live
// heads floor at 28px so started reads longer than draft.
// Matches the sheen duration in global.css so every rail shares one phase.
const SHIMMER_PERIOD_MS = 1800;

function shimmerSyncStyle() {
    return {
        animationDelay: `-${performance.now() % SHIMMER_PERIOD_MS}ms`,
    };
}

function ShiftChipOutreachRailBar({
    rail,
}: {
    readonly rail: ShiftChipOutreachRail;
}) {
    if (rail.kind === 'tone') {
        return (
            <span
                aria-hidden={true}
                className={cn(
                    'calendar-outreach-rail-sheen absolute inset-x-2 bottom-1.5 h-1 rounded-full',
                    rail.tone === 'good'
                        ? 'bg-[#0F9A55] dark:bg-[#34D399]'
                        : 'bg-[#FF785C]',
                )}
                style={shimmerSyncStyle()}
            />
        );
    }
    const fillPercent =
        rail.kind === 'live'
            ? Math.min(1, Math.max(0, rail.fraction)) * 100
            : 0;
    return (
        <span
            aria-hidden={true}
            className="absolute inset-x-2 bottom-1.5 h-1 rounded-full bg-[#5533EE]/15 dark:bg-[#8C82FF]/25"
        >
            <span
                className={cn(
                    'absolute inset-y-0 left-0 rounded-full bg-[#5533EE] dark:bg-[#8C82FF]',
                    rail.kind === 'live'
                        ? 'calendar-outreach-rail-sheen min-w-7'
                        : 'min-w-3.5',
                )}
                style={{
                    width: `${fillPercent}%`,
                    ...(rail.kind === 'live' ? shimmerSyncStyle() : {}),
                }}
            />
        </span>
    );
}

// Always-visible checkbox overlay for open shifts, shared by the week-grid
// chips and the day-timeline blocks; positions itself against the nearest
// `group/shift-chip` ancestor.
export function ShiftSelectionCheckbox({
    shift,
    outreachStatus,
    dateLabel,
    timeRange,
    isSelected,
    isAtSelectionCap,
    maximumSelectedShifts,
    now,
    className,
    onToggle,
}: {
    readonly shift: Shift;
    readonly outreachStatus: ShiftOutreachStatus | undefined;
    readonly dateLabel: string;
    readonly timeRange: string;
    readonly isSelected: boolean;
    readonly isAtSelectionCap: boolean;
    readonly maximumSelectedShifts: number;
    readonly now: Date;
    // Callers that position the checkbox themselves (the mobile agenda docks it
    // in its own full-height tap column rather than over a chip's corner)
    // override the default absolute placement.
    readonly className?: string;
    readonly onToggle?: (shift: Shift) => void;
}) {
    const { formatName } = useFormatPersonName();
    const eligibility = getCalendarShiftSelectionEligibility(
        shift,
        outreachStatus,
        isSelected,
        isAtSelectionCap,
        maximumSelectedShifts,
        now,
    );
    const checkbox = (
        <span
            className={cn(
                'focus-visible:ring-ring absolute top-1/2 right-2 z-10 flex -translate-y-1/2 items-center rounded-sm focus-visible:ring-2 focus-visible:outline-none',
                className,
            )}
            tabIndex={eligibility.isEligible ? undefined : 0}
            aria-label={eligibility.disabledReason ?? undefined}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
        >
            <Checkbox
                data-testid="calendar-shift-checkbox"
                data-shift-id={shift.id}
                data-client-name={shift.patient_name}
                data-selection-eligible={eligibility.isEligible}
                className="border-black/40 bg-white motion-reduce:transition-none dark:border-white/70 dark:bg-white"
                checked={isSelected}
                disabled={!eligibility.isEligible}
                aria-label={
                    isSelected
                        ? `Remove ${formatName(shift.patient_name)}, ${dateLabel}, ${timeRange} from outreach selection`
                        : `Add ${formatName(shift.patient_name)}, ${dateLabel}, ${timeRange} to outreach selection`
                }
                onCheckedChange={() => onToggle?.(shift)}
            />
        </span>
    );

    return eligibility.disabledReason ? (
        <Tooltip>
            <TooltipTrigger asChild={true}>{checkbox}</TooltipTrigger>
            <TooltipContent>{eligibility.disabledReason}</TooltipContent>
        </Tooltip>
    ) : (
        checkbox
    );
}

const SHIFT_DATE_LABEL_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function getShiftDateLabelFormatter(timezone: string): Intl.DateTimeFormat {
    let formatter = SHIFT_DATE_LABEL_FORMATTERS.get(timezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        });
        SHIFT_DATE_LABEL_FORMATTERS.set(timezone, formatter);
    }
    return formatter;
}

export function formatCompactTimeRange(shift: Shift, timezone: string): string {
    return `${formatCompactTime(shift.shift_start_time, timezone)}–${formatCompactTime(shift.shift_end_time, timezone)}`;
}

export function formatShiftDateLabel(shift: Shift, timezone: string): string {
    return getShiftDateLabelFormatter(timezone).format(
        new Date(shift.shift_start_time),
    );
}

function formatShiftAriaLabel(
    shift: Shift,
    dateLabel: string,
    timeRange: string,
    caregiverLabel: string | null,
    statusLabel: string,
    recurrenceLabel: string | null,
    groupSize: number | null,
): string {
    return [
        shift.patient_name,
        dateLabel,
        timeRange,
        caregiverLabel,
        statusLabel,
        recurrenceLabel,
        groupSize !== null ? 'part of a multi-shift outreach' : null,
    ]
        .filter(Boolean)
        .join(', ');
}
